import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { updateMyProfileName } from '../../lib/apiClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * **Complete your profile**, copied from production's Welcome screen.
 *
 * Production asks for a first and last name before the customer can continue,
 * and every surface that prints a name reads it: the dashboard greeting, the
 * report's "Prepared for", the workbook cover, and the admin Users table. V2
 * created the profile row empty and never wrote a name into it, so all four
 * were permanently blank — including the ones this migration had already built.
 *
 * Both fields are required, which is production's rule, and the refusal is
 * production's wording.
 */
export function ProfileNameForm({
  token,
  onSaved,
}: {
  token: string;
  /** Called once the name is stored, so the caller can continue. */
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (saving) return;
    if (firstName.trim() === '' || lastName.trim() === '') {
      setError('Both fields are required.');
      return;
    }

    setSaving(true);
    setError(null);
    const res = await updateMyProfileName(token, { firstName, lastName });
    setSaving(false);

    if (res.state === 'invalid') {
      setError('Both fields are required.');
      return;
    }
    if (res.state !== 'ok') {
      setError('Something went wrong. Please try again.');
      return;
    }
    onSaved();
  }

  return (
    <section className="mx-auto flex w-full max-w-md flex-col gap-5" data-testid="profile-name-form">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Complete your profile</h1>
        <p className="text-sm text-muted-foreground">
          Before you continue, add your first and last name so your assessment history and reports
          stay organized.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium" htmlFor="profile-first-name">
          First name
        </label>
        <Input
          id="profile-first-name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          placeholder="e.g. Alex"
          data-testid="profile-first-name"
          autoComplete="given-name"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium" htmlFor="profile-last-name">
          Last name
        </label>
        <Input
          id="profile-last-name"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          placeholder="e.g. Morgan"
          data-testid="profile-last-name"
          autoComplete="family-name"
        />
      </div>

      <Button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        data-testid="profile-save"
        className="self-start"
      >
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        Continue
      </Button>

      {error ? (
        <p className="text-sm font-medium text-destructive" data-testid="profile-error" role="status">
          {error}
        </p>
      ) : null}
    </section>
  );
}
